import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatPrice, resolvePhoto } from "@/lib/api";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { formatDateFr, formatDateTimeFr } from "@/components/DateFieldFr";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SellerBadge } from "@/components/SellerBadge";
import { RstAdminPanel } from "@/components/RstAdminPanel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CascadeSelect } from "@/components/CascadeSelect";
import { toast } from "sonner";
import {
  Users, Store, Clock, Package, MessagesSquare, ShieldCheck, ShieldX, Gem, Gavel, ShieldPlus,
  Truck, Wallet, Image as ImageIcon, Phone, MessageCircle, Search, FileText, Check, X, History, MapPin, Mail,
  Briefcase, Trash2, Power, UserPlus, ShoppingBag, Megaphone, Ban, Play, Pause, Pencil,
  Eye, Calendar, ClipboardList, AlertTriangle,
} from "lucide-react";

const StatCard = ({ Icon, label, value, onClick, danger }) => {
  const active = danger && value > 0;
  const base = "rounded-2xl border p-5 text-left w-full transition-all";
  const cls = active
    ? "bg-destructive/10 border-destructive/40 hover:border-destructive"
    : "bg-card border-border hover:border-primary/50";
  const clickable = onClick ? "cursor-pointer hover:shadow-md active:scale-[0.99]" : "";
  return (
    <button type="button" onClick={onClick} disabled={!onClick} className={`${base} ${cls} ${clickable}`} data-testid={`stat-${label}`}>
      <span className={`h-10 w-10 rounded-xl flex items-center justify-center ${active ? "bg-destructive/15" : "bg-primary/10"}`}>
        <Icon className={`h-5 w-5 ${active ? "text-destructive" : "text-primary"}`} />
      </span>
      <p className={`mt-3 font-heading font-extrabold text-2xl ${active ? "text-destructive" : "text-secondary"}`}>{value}</p>
      <p className={`text-sm ${active ? "text-destructive font-semibold" : "text-muted-foreground"}`}>{label}</p>
    </button>
  );
};

const waLink = (phone) => `https://wa.me/${(phone || "").replace(/[^0-9]/g, "")}`;

const ContactButtons = ({ phone, label, testid }) => {
  const { t } = useT();
  if (!phone) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {label && <span className="text-xs font-semibold text-muted-foreground">{label} :</span>}
      <a href={`tel:${phone}`} data-testid={`call-${testid}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-secondary hover:bg-muted">
        <Phone className="h-4 w-4" /> {phone}
      </a>
      <a href={waLink(phone)} target="_blank" rel="noreferrer" data-testid={`wa-${testid}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-success px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90">
        <MessageCircle className="h-4 w-4" /> {t("admin.whatsapp")}
      </a>
    </div>
  );
};

const UserInfo = ({ u }) => {
  const { t } = useT();
  return (
    <div className="text-sm text-muted-foreground space-y-0.5 mt-1">
      <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> <span className="font-medium text-foreground">{u.email || "—"}</span></p>
      <p className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {u.phone || "—"}</p>
      <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {[u.neighborhood, u.city, u.region].filter(Boolean).join(", ") || "—"}</p>
      {u.created_at && <p className="flex items-center gap-1.5" data-testid={`user-registered-${u.id}`}><Calendar className="h-3.5 w-3.5" /> {t("admin.registeredAt")} : <span className="font-medium text-foreground">{formatDateTimeFr(u.created_at)}</span></p>}
      {u.role === "buyer" && (u.activity_type || u.profession) && (
        <p className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />
          {[u.activity_type && `${t("admin.activity")}: ${u.activity_type}`, u.profession && `${t("admin.profession")}: ${u.profession}`].filter(Boolean).join(" · ")}
        </p>
      )}
      {u.role === "seller" && ((Array.isArray(u.specialty) ? u.specialty.length : u.specialty) || u.farm_type || u.farm_size) && (
        <p className="flex items-center gap-1.5"><Store className="h-3.5 w-3.5" />
          {[
            (Array.isArray(u.specialty) ? u.specialty.length : u.specialty) &&
              `${t("admin.specialty")}: ${Array.isArray(u.specialty) ? u.specialty.map((s) => t(`categories.${s}`)).join(", ") : u.specialty}`,
            u.farm_type, u.farm_size,
          ].filter(Boolean).join(" · ")}
        </p>
      )}
      {u.role === "seller" && u.payout_methods && (u.payout_methods.orange_money?.enabled || u.payout_methods.wave_money?.enabled || u.payout_methods.moov_money?.enabled || u.payout_methods.bank?.enabled) && (
        <div className="flex items-start gap-1.5" data-testid={`payout-info-${u.id}`}>
          <Wallet className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span className="text-foreground">
            <span className="font-semibold">{t("admin.payout")}: </span>
            {[
              u.payout_methods.orange_money?.enabled && `Orange Money${u.payout_methods.orange_money.number ? ` (${u.payout_methods.orange_money.number})` : ""}`,
              u.payout_methods.wave_money?.enabled && `Wave Money${u.payout_methods.wave_money.number ? ` (${u.payout_methods.wave_money.number})` : ""}`,
              u.payout_methods.moov_money?.enabled && `Moov Money${u.payout_methods.moov_money.number ? ` (${u.payout_methods.moov_money.number})` : ""}`,
              u.payout_methods.bank?.enabled && `Virement${u.payout_methods.bank.rib ? ` (${u.payout_methods.bank.rib})` : ""}`,
            ].filter(Boolean).join(" · ")}
          </span>
        </div>
      )}
      {u.nif && <p>{t("admin.nif")}: {u.nif}</p>}
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        <span className="text-xs">{t("admin.documents")}:</span>
        {(u.documents && u.documents.length) ? u.documents.map((d, i) => (
          <a key={i} href={resolvePhoto(d)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-xs" data-testid={`doc-${u.id}-${i}`}>
            <FileText className="h-3.5 w-3.5" /> {i + 1}
          </a>
        )) : <span className="text-xs italic">{t("admin.noDocuments")}</span>}
      </div>
    </div>
  );
};

const ConfirmDeleteButton = ({ onConfirm, testid, label, title, desc }) => {
  const { t } = useT();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-destructive" data-testid={testid}>
          <Trash2 className="h-4 w-4 mr-1.5" /> {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`${testid}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{desc}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testid}-cancel`}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={onConfirm} data-testid={`${testid}-confirm`}>{label}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const DeleteProfileButton = ({ userId, onDelete }) => {
  const { t } = useT();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" className="text-destructive border-destructive/40 hover:bg-destructive/5" data-testid={`delete-user-${userId}`}>
          <Trash2 className="h-4 w-4 mr-1.5" /> {t("admin.deleteProfile")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`delete-dialog-${userId}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("admin.deleteConfirmTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("admin.deleteConfirmDesc")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`delete-cancel-${userId}`}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={() => onDelete(userId)} data-testid={`delete-confirm-${userId}`}>
            {t("admin.deleteProfile")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const DateChip = ({ Icon, label, iso }) => {
  if (!iso) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-muted rounded-md px-1.5 py-0.5">
      <Icon className="h-3 w-3" /> {label} : <span className="font-semibold text-secondary">{fmtDate(iso)}</span>
    </span>
  );
};

const ProfileDialog = ({ open, onOpenChange, data, loading, cur }) => {
  const { t } = useT();
  const isSeller = data?.role === "seller";
  const u = data?.user;
  const stats = data?.stats || {};
  const orderStatusCls = (s) => {
    if (["delivered", "closed"].includes(s)) return "bg-success/15 text-success";
    if (["refused", "cancelled", "refunded", "disputed"].includes(s)) return "bg-destructive/10 text-destructive";
    return "bg-amber-100 text-amber-700";
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-secondary flex items-center gap-2">
            <Eye className="h-5 w-5" /> {isSeller ? t("admin.profileSellerTitle") : t("admin.profileBuyerTitle")}
          </DialogTitle>
          <DialogDescription>{u?.name}</DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <p className="text-sm text-muted-foreground py-10 text-center" data-testid="profile-loading">{t("admin.profileLoading")}</p>
        ) : (
          <div className="space-y-5">
            {/* Contact (admin only) */}
            <div className="rounded-xl bg-muted/50 border border-border p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1.5">{t("admin.profileContact")}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-secondary">
                {u?.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{u.phone}</span>}
                {u?.email && <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{u.email}</span>}
                {(u?.city || u?.neighborhood || u?.region) && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{[u?.neighborhood, u?.city, u?.region].filter(Boolean).join(", ")}</span>}
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-3 gap-3" data-testid="profile-kpis">
              <div className="rounded-xl bg-card border border-border p-3 text-center">
                <p className="font-heading font-extrabold text-2xl text-foreground">{stats.orders_total ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t("admin.profileKpiOrders")}</p>
              </div>
              <div className="rounded-xl bg-card border border-border p-3 text-center">
                <p className="font-heading font-extrabold text-2xl text-success">{stats.finalized ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t("admin.profileKpiFinalized")}</p>
              </div>
              <div className="rounded-xl bg-card border border-border p-3 text-center">
                <p className="font-heading font-extrabold text-lg text-foreground">{formatPrice(stats.amount ?? 0, cur)}</p>
                <p className="text-xs text-muted-foreground">{isSeller ? t("admin.profileKpiRevenue") : t("admin.profileKpiSpend")}</p>
              </div>
            </div>

            {/* Listings (sellers only) */}
            {isSeller && (
              <div>
                <p className="font-heading font-bold text-secondary mb-2 flex items-center gap-1.5"><Package className="h-4 w-4" /> {t("admin.profileListings")}</p>
                {data.listings.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center" data-testid="profile-no-listings">{t("admin.profileNoListings")}</p>
                ) : (
                  <div className="grid gap-2" data-testid="profile-listings">
                    {data.listings.map((l) => {
                      const age = daysSince(l.created_at);
                      const longOnline = l.status === "active" && age != null && age >= 7;
                      return (
                        <div key={l.id} className={`rounded-xl border p-3 flex items-center gap-3 ${longOnline ? "border-destructive/40 bg-destructive/5" : "border-border bg-card"}`} data-testid={`profile-listing-${l.id}`}>
                          <img src={resolvePhoto(l.image)} alt="" className="h-12 w-12 rounded-lg object-cover pointer-events-none shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-secondary text-sm truncate">{l.title}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <DateChip Icon={Calendar} label={t("admin.profilePublishedOn")} iso={l.created_at} />
                              {l.sold_at && <DateChip Icon={Check} label={t("admin.profileSoldOn")} iso={l.sold_at} />}
                              {age != null && l.status === "active" && (
                                <span className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-md px-1.5 py-0.5 ${longOnline ? "bg-destructive/15 text-destructive" : "bg-secondary/10 text-secondary"}`} data-testid={`listing-age-${l.id}`}>
                                  {longOnline ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                                  {t(longOnline ? "admin.profileLongOnline" : "admin.profileOnlineSince", { n: age })}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">{formatPrice(l.price, cur)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Orders history */}
            <div>
              <p className="font-heading font-bold text-secondary mb-2 flex items-center gap-1.5"><ClipboardList className="h-4 w-4" /> {isSeller ? t("admin.profileOrdersSeller") : t("admin.profileOrdersBuyer")}</p>
              {data.orders.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center" data-testid="profile-no-orders">{t("admin.profileNoOrders")}</p>
              ) : (
                <div className="grid gap-2" data-testid="profile-orders">
                  {data.orders.map((o) => (
                    <div key={o.id} className="rounded-xl border border-border bg-card p-3" data-testid={`profile-order-${o.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-secondary text-sm truncate">{o.listing_title}</p>
                          <p className="text-xs text-muted-foreground">{isSeller ? o.buyer_name : o.seller_name} · {o.quantity ? `${o.quantity} · ` : ""}{formatPrice(o.grand_total || o.total_amount, cur)}</p>
                        </div>
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 ${orderStatusCls(o.status)}`} data-testid={`profile-order-status-${o.id}`}>{t(`order.statuses.${o.status}`)}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <DateChip Icon={Calendar} label={t("admin.profileDateCreated")} iso={o.created_at} />
                        <DateChip Icon={Wallet} label={t("admin.profileDatePaid")} iso={o.paid_at} />
                        <DateChip Icon={Truck} label={t("admin.profileDateShipped")} iso={o.shipped_at} />
                        <DateChip Icon={Check} label={t("admin.profileDateDelivered")} iso={o.delivered_at} />
                        <DateChip Icon={History} label={t("admin.profileDateClosed")} iso={o.closed_at} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


export default function AdminDashboard() {
  const brand = useBrand();
  const { t } = useT();
  const [stats, setStats] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [listings, setListings] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [finance, setFinance] = useState({ orders: [], payouts: [], totals: {} });
  const [offers, setOffers] = useState([]);
  const [violations, setViolations] = useState({ offenders: [], total_attempts: 0 });
  const [categories, setCategories] = useState([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [disputeSettings, setDisputeSettings] = useState({ default_admin_id: "", default_admin_name: "", admins: [] });
  const [threadDispute, setThreadDispute] = useState(null);
  const [dmsg, setDmsg] = useState("");
  const [dmsgBusy, setDmsgBusy] = useState(false);
  const emptyOfferForm = { buyer_id: "", buyer_name: "", category: "", quantity: 10, region: "", commune: "", neighborhood: "", needed_by: "", note: "", publish: true, status: "pending" };
  const [offerForm, setOfferForm] = useState(emptyOfferForm);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [offerBusy, setOfferBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [feeInputs, setFeeInputs] = useState({});
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "pending-sellers");

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users?role=seller").then((r) => setSellers(r.data));
    api.get("/admin/users?role=buyer").then((r) => setBuyers(r.data));
    api.get("/admin/listings").then((r) => setListings(r.data));
    api.get("/admin/disputes").then((r) => setDisputes(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/finance").then((r) => setFinance(r.data)).catch(() => {});
    api.get("/admin/offers").then((r) => setOffers(r.data)).catch(() => {});
    api.get("/admin/contact-violations").then((r) => setViolations(r.data)).catch(() => {});
    api.get("/admin/dispute-settings").then((r) => setDisputeSettings(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);
  useAutoRefresh(loadAll, 15000);

  const closeOrder = async (id) => { await api.post(`/admin/orders/${id}/close`); toast.success(t("admin.closed")); loadAll(); };

  const cur = brand.currencySymbol;
  const match = (obj, keys) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return keys.some((k) => (obj[k] || "").toString().toLowerCase().includes(s));
  };

  const setVerify = async (id, verified) => { await api.post(`/admin/users/${id}/verify`, { verified }); toast.success(verified ? "Vendeur vérifié" : "Vérification retirée"); loadAll(); };
  const deleteUser = async (id) => { await api.delete(`/admin/users/${id}`); toast.success(t("admin.profileDeleted")); loadAll(); };
  const openProfile = async (id) => {
    setProfileOpen(true); setProfileLoading(true); setProfileData(null);
    try { const r = await api.get(`/admin/users/${id}/profile`); setProfileData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); setProfileOpen(false); }
    finally { setProfileLoading(false); }
  };
  const blockUser = async (id, blocked) => { await api.post(`/admin/users/${id}/block`, { blocked }); toast.success(blocked ? t("admin.blockedDone") : t("admin.unblockedDone")); loadAll(); };
  const setOfferStatus = async (id, status) => { await api.patch(`/admin/offers/${id}/status`, { status }); toast.success(t("admin.offerUpdated")); loadAll(); };
  const deleteOffer = async (id) => { await api.delete(`/admin/offers/${id}`); toast.success(t("admin.offerDeleted")); loadAll(); };
  const openCreateOffer = () => { setEditId(null); setOfferForm(emptyOfferForm); setOfferDialogOpen(true); };
  const openEditOffer = (o) => {
    setEditId(o.id);
    setOfferForm({
      buyer_id: o.buyer_id || "", buyer_name: o.buyer_name || "", category: o.category || "",
      quantity: o.quantity || 1, region: o.region || "", commune: o.commune || "",
      neighborhood: o.neighborhood || "", needed_by: o.needed_by || "", note: o.note || "",
      publish: o.status === "published", status: o.status || "pending", external_buyer: !!o.external_buyer,
    });
    setOfferDialogOpen(true);
  };
  const submitOffer = async () => {
    if (!editId && !offerForm.buyer_id && !offerForm.buyer_name?.trim()) { toast.error(t("admin.offerBuyerOrName")); return; }
    if (!offerForm.category) { toast.error(t("admin.offerPickProduct")); return; }
    if (!offerForm.neighborhood?.trim()) { toast.error(t("common.required")); return; }
    setOfferBusy(true);
    try {
      if (editId) {
        await api.patch(`/admin/offers/${editId}`, {
          buyer_name: offerForm.buyer_name, category: offerForm.category, quantity: Number(offerForm.quantity),
          region: offerForm.region, commune: offerForm.commune, neighborhood: offerForm.neighborhood,
          needed_by: offerForm.needed_by, note: offerForm.note, status: offerForm.status,
        });
        toast.success(t("admin.offerUpdated"));
      } else {
        await api.post("/admin/offers", { ...offerForm, quantity: Number(offerForm.quantity) });
        toast.success(t("admin.offerCreated"));
      }
      setOfferDialogOpen(false); setEditId(null); setOfferForm(emptyOfferForm); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setOfferBusy(false); }
  };
  const setPremium = async (id, premium) => { await api.post(`/admin/users/${id}/premium`, { premium }); toast.success(premium ? "Premium RST attribué" : "Premium retiré"); loadAll(); };
  const resolveDispute = async (id, resolution) => { await api.post(`/disputes/${id}/resolve`, { resolution }); toast.success("Litige tranché"); loadAll(); };
  const setDefaultDisputeAdmin = async (admin_id) => {
    try { const r = await api.put("/admin/dispute-settings", { admin_id }); setDisputeSettings((s) => ({ ...s, default_admin_id: r.data.default_admin_id, default_admin_name: r.data.default_admin_name })); toast.success(t("admin.disputeDefaultSaved")); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const transferDispute = async (id, admin_id) => {
    try { await api.post(`/admin/disputes/${id}/assign`, { admin_id }); toast.success(t("admin.disputeTransferred")); const r = await api.get("/admin/disputes"); setDisputes(r.data); setThreadDispute((td) => td ? r.data.find((x) => x.id === td.id) || td : td); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const sendDisputeMsg = async () => {
    if (!dmsg.trim() || !threadDispute) return;
    setDmsgBusy(true);
    try { await api.post(`/disputes/${threadDispute.id}/messages`, { body: dmsg }); setDmsg(""); const r = await api.get("/admin/disputes"); setDisputes(r.data); setThreadDispute(r.data.find((x) => x.id === threadDispute.id) || threadDispute); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setDmsgBusy(false); }
  };
  const saveFee = async (id) => { await api.put(`/admin/orders/${id}/delivery-fee`, { fee: Number(feeInputs[id] ?? 0) }); toast.success(t("admin.feeSaved")); loadAll(); };
  const validatePayment = async (id) => { await api.post(`/admin/orders/${id}/validate-payment`); toast.success(t("admin.paymentValidated")); loadAll(); };
  const approveListing = async (id) => { try { await api.post(`/admin/listings/${id}/approve`); toast.success(t("admin.listingApproved")); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  const rejectListing = async (id) => { try { await api.post(`/admin/listings/${id}/reject`); toast.success(t("admin.listingRejected")); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  const toggleListing = async (id) => { try { const r = await api.patch(`/admin/listings/${id}/status`); toast.success(r.data.status === "paused" ? t("admin.listingSuspended") : t("admin.listingReactivated")); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  const deleteListing = async (id) => { try { await api.delete(`/admin/listings/${id}`); toast.success(t("admin.listingDeleted")); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };

  const sellersF = useMemo(() => sellers.filter((s) => match(s, ["name", "email", "phone", "city", "neighborhood"])).sort((a, b) => (a.verified === b.verified ? 0 : a.verified ? 1 : -1)), [sellers, q]);
  const pendingSellers = useMemo(() => sellersF.filter((s) => !s.verified), [sellersF]);
  const verifiedSellers = useMemo(() => sellersF.filter((s) => s.verified), [sellersF]);

  const renderSeller = (s) => (
    <div key={s.id} className="rounded-xl bg-card border border-border p-4" data-testid={`admin-seller-${s.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading font-semibold text-secondary flex items-center gap-2 flex-wrap">
            {s.name}
            {s.verified
              ? <Badge className="bg-secondary/10 text-secondary border-0">{t("admin.sellerVerified")}</Badge>
              : <Badge className="bg-accent/15 text-accent-foreground border-0">{t("admin.sellerUnverified")}</Badge>}
            <SellerBadge badge={s.badge} premium={s.premium} size="sm" />
          </div>
          <UserInfo u={s} />
        </div>
        <div className="flex flex-col items-end gap-2">
          <ContactButtons phone={s.phone} testid={s.id} />
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" size="sm" className="text-secondary" onClick={() => openProfile(s.id)} data-testid={`view-profile-${s.id}`}><Eye className="h-4 w-4 mr-1.5" /> {t("admin.viewProfile")}</Button>
            <Button variant={s.premium ? "outline" : "ghost"} size="sm" onClick={() => setPremium(s.id, !s.premium)} data-testid={`premium-${s.id}`} className={s.premium ? "text-destructive" : "text-primary"}>
              <Gem className="h-4 w-4 mr-1.5" /> {s.premium ? t("admin.removePremium") : t("admin.makePremium")}
            </Button>
            {s.verified ? (
              <Button variant="outline" size="sm" className="text-destructive" onClick={() => setVerify(s.id, false)} data-testid={`unverify-${s.id}`}><ShieldX className="h-4 w-4 mr-1.5" /> {t("admin.unverify")}</Button>
            ) : (
              <Button size="sm" className="bg-secondary hover:bg-secondary/90" onClick={() => setVerify(s.id, true)} data-testid={`verify-${s.id}`}><ShieldCheck className="h-4 w-4 mr-1.5" /> {t("admin.verify")}</Button>
            )}
            <DeleteProfileButton userId={s.id} onDelete={deleteUser} />
          </div>
        </div>
      </div>
    </div>
  );
  const buyersF = useMemo(() => buyers.filter((b) => match(b, ["name", "email", "phone", "city", "neighborhood"])), [buyers, q]);
  const pendingOffersCount = useMemo(() => offers.filter((o) => o.status === "pending").length, [offers]);
  const manageListings = useMemo(() => listings.filter((l) => match(l, ["seller_name", "location", "seller_phone", "title"])), [listings, q]);
  const pendingCount = useMemo(() => listings.filter((l) => l.status === "pending").length, [listings]);
  const history = useMemo(() => orders.filter((o) => ["delivered", "refunded", "cancelled"].includes(o.status) && match(o, ["listing_title", "buyer_name", "seller_name"])), [orders, q]);

  return (
    <div className="container-page py-10">
      <h1 className="font-heading font-extrabold text-3xl text-secondary">{t("admin.title")}</h1>
      <p className="text-muted-foreground mt-1">{t("admin.subtitle")}</p>

      {stats && (
        <div className="flex flex-wrap items-center gap-3 mt-5" data-testid="admin-today-summary">
          <span className="text-sm font-semibold text-secondary">{t("admin.today")} :</span>
          <button
            onClick={() => setTab("pending-sellers")}
            data-testid="today-new-users"
            className="inline-flex items-center gap-2 rounded-full bg-secondary/10 text-secondary px-3.5 py-1.5 text-sm font-semibold hover:bg-secondary/20 transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            <span className="tabular-nums">{stats.new_users_today ?? 0}</span>
            {t("admin.newRegistrations")}
          </button>
          <button
            onClick={() => setTab("orders")}
            data-testid="today-new-orders"
            className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3.5 py-1.5 text-sm font-semibold hover:bg-primary/20 transition-colors"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="tabular-nums">{stats.new_orders_today ?? 0}</span>
            {t("admin.newOrders")}
          </button>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
          <StatCard Icon={Users} label={t("admin.buyers")} value={stats.buyers} onClick={() => setTab("buyers")} />
          <StatCard Icon={Store} label={t("admin.sellers")} value={stats.sellers} onClick={() => setTab("verified-sellers")} />
          <StatCard Icon={Clock} label={t("admin.pending")} value={stats.pending_sellers} onClick={() => setTab("pending-sellers")} danger />
          <StatCard Icon={Package} label={t("admin.listings")} value={stats.listings} onClick={() => setTab("pendinglistings")} />
          <StatCard Icon={MessagesSquare} label={t("admin.conversations")} value={stats.conversations} onClick={() => setTab("orders")} />
        </div>
      )}

      {/* Global search */}
      <div className="mt-6 relative max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.searchPlaceholder")} className="pl-9 h-12" data-testid="admin-search" />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="pending-sellers" data-testid="tab-pending-sellers" className={stats && stats.pending_sellers > 0 ? "data-[state=inactive]:text-destructive" : ""}><Clock className="h-4 w-4 mr-1.5" /> {t("admin.pendingSellersTab")}{stats && stats.pending_sellers > 0 && <span className="ml-1.5 text-[10px] font-bold bg-destructive text-white rounded-full px-1.5 py-0.5" data-testid="pending-sellers-count">{stats.pending_sellers}</span>}</TabsTrigger>
          <TabsTrigger value="verified-sellers" data-testid="tab-verified-sellers"><Store className="h-4 w-4 mr-1.5" /> {t("admin.verifiedSellersTab")}{verifiedSellers.length > 0 && <span className="ml-1.5 text-[10px] font-bold bg-secondary text-white rounded-full px-1.5 py-0.5" data-testid="verified-sellers-count">{verifiedSellers.length}</span>}</TabsTrigger>
          <TabsTrigger value="pendinglistings" data-testid="tab-pending-listings"><Package className="h-4 w-4 mr-1.5" /> {t("admin.listingsTab")}{pendingCount > 0 && <span className="ml-1.5 text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5 py-0.5" data-testid="pending-listings-count">{pendingCount}</span>}</TabsTrigger>
          <TabsTrigger value="buyers" data-testid="tab-buyers"><Users className="h-4 w-4 mr-1.5" /> {t("admin.buyersTab")}</TabsTrigger>
          <TabsTrigger value="offers" data-testid="tab-offers"><Megaphone className="h-4 w-4 mr-1.5" /> {t("admin.offersTab")}{pendingOffersCount > 0 && <span className="ml-1.5 text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5 py-0.5" data-testid="pending-offers-count">{pendingOffersCount}</span>}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders"><Truck className="h-4 w-4 mr-1.5" /> {t("admin.ordersTab")}</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="tab-disputes"><Gavel className="h-4 w-4 mr-1.5" /> {t("admin.disputesTab")}</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History className="h-4 w-4 mr-1.5" /> {t("admin.historyTab")}</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance"><Wallet className="h-4 w-4 mr-1.5" /> {t("admin.financeTab")}</TabsTrigger>
          <TabsTrigger value="rst" data-testid="tab-rst"><ShieldPlus className="h-4 w-4 mr-1.5" /> {t("admin.rstTab")}</TabsTrigger>
          <TabsTrigger value="violations" data-testid="tab-violations"><ShieldX className="h-4 w-4 mr-1.5" /> {t("admin.violationsTab")}{violations.offenders.length > 0 && <span className="ml-1.5 text-[10px] font-bold bg-destructive text-white rounded-full px-1.5 py-0.5" data-testid="violations-count">{violations.offenders.length}</span>}</TabsTrigger>
        </TabsList>

        {/* Pending sellers */}
        <TabsContent value="pending-sellers" className="mt-6">
          <div className="grid gap-3">
            {pendingSellers.length === 0
              ? <p className="text-muted-foreground text-sm py-10 text-center" data-testid="no-pending-sellers">{t("admin.noPendingSellers")}</p>
              : pendingSellers.map(renderSeller)}
          </div>
        </TabsContent>

        {/* Verified sellers */}
        <TabsContent value="verified-sellers" className="mt-6">
          <div className="grid gap-3">
            {verifiedSellers.length === 0
              ? <p className="text-muted-foreground text-sm py-10 text-center" data-testid="no-verified-sellers">{t("admin.noVerifiedSellers")}</p>
              : verifiedSellers.map(renderSeller)}
          </div>
        </TabsContent>

        {/* Listings management */}
        <TabsContent value="pendinglistings" className="mt-6">
          <div className="grid gap-3">
            {manageListings.length === 0 ? <p className="text-muted-foreground text-sm py-10 text-center" data-testid="no-pending-listings">{t("admin.noListings")}</p> : manageListings.map((l) => {
              const statusMap = {
                pending: { label: t("admin.statusPending"), cls: "bg-amber-100 text-amber-700" },
                active: { label: t("admin.statusActive"), cls: "bg-success/15 text-success" },
                paused: { label: t("admin.statusPaused"), cls: "bg-muted text-muted-foreground" },
                rejected: { label: t("admin.statusRejected"), cls: "bg-destructive/10 text-destructive" },
                sold: { label: t("admin.statusSold"), cls: "bg-secondary/15 text-secondary" },
              };
              const st = statusMap[l.status] || statusMap.pending;
              const needsAction = l.status === "pending";
              return (
              <div key={l.id} className={`rounded-xl border p-4 flex flex-wrap items-center gap-4 ${needsAction ? "border-destructive/50 bg-destructive/5 ring-1 ring-destructive/20" : "bg-card border-border"}`} data-testid={`admin-pending-listing-${l.id}`}>
                <img src={resolvePhoto(l.image)} alt="" className="h-16 w-16 rounded-lg object-cover pointer-events-none" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-secondary">{l.title}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`listing-status-${l.id}`}>{st.label}</span>
                    {needsAction && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-destructive text-white" data-testid={`listing-todo-${l.id}`}>{t("admin.toProcess")}</span>}
                  </div>
                  <p className="text-sm text-muted-foreground">{l.seller_name} · {l.location} · {formatPrice(l.price, cur)} / {l.price_unit}</p>
                  {l.created_at && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1" data-testid={`listing-created-${l.id}`}><Calendar className="h-3 w-3" /> {t("admin.createdAt")} : {formatDateTimeFr(l.created_at)}</p>}
                  <div className="mt-1"><ContactButtons phone={l.seller_phone} label={t("admin.seller")} testid={`ls-${l.id}`} /></div>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  {l.status === "pending" && (
                    <>
                      <Button size="sm" className="bg-success hover:bg-success/90 text-white" onClick={() => approveListing(l.id)} data-testid={`approve-${l.id}`}><Check className="h-4 w-4 mr-1.5" /> {t("admin.approve")}</Button>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => rejectListing(l.id)} data-testid={`reject-${l.id}`}><X className="h-4 w-4 mr-1.5" /> {t("admin.reject")}</Button>
                    </>
                  )}
                  {(l.status === "active" || l.status === "paused") && (
                    <Button size="sm" variant="outline" className="text-secondary" onClick={() => toggleListing(l.id)} data-testid={`toggle-${l.id}`}>
                      <Power className="h-4 w-4 mr-1.5" /> {l.status === "active" ? t("admin.suspend") : t("admin.reactivate")}
                    </Button>
                  )}
                  {l.status === "rejected" && (
                    <Button size="sm" className="bg-success hover:bg-success/90 text-white" onClick={() => approveListing(l.id)} data-testid={`approve-${l.id}`}><Check className="h-4 w-4 mr-1.5" /> {t("admin.approve")}</Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/5" data-testid={`delete-listing-${l.id}`}><Trash2 className="h-4 w-4 mr-1.5" /> {t("admin.delete")}</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid={`delete-listing-dialog-${l.id}`}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("admin.deleteListingTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("admin.deleteListingDesc")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={() => deleteListing(l.id)} data-testid={`delete-listing-confirm-${l.id}`}>{t("admin.delete")}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );})}
          </div>
        </TabsContent>

        {/* Buyers */}
        <TabsContent value="buyers" className="mt-6">
          <div className="grid gap-3">
            {buyersF.length === 0 ? <p className="text-muted-foreground text-sm py-10 text-center" data-testid="no-buyers">{t("admin.noBuyers")}</p> : buyersF.map((b) => (
              <div key={b.id} className="rounded-xl bg-card border border-border p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`admin-buyer-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-secondary flex items-center gap-2 flex-wrap">
                    {b.name}
                    {b.activity_type && <Badge className="bg-muted text-muted-foreground border-0">{b.activity_type}</Badge>}
                    {b.blocked && <Badge className="bg-destructive/10 text-destructive border-0" data-testid={`blocked-badge-${b.id}`}>{t("admin.blockedLabel")}</Badge>}
                  </div>
                  <UserInfo u={b} />
                </div>
                <div className="flex flex-col items-end gap-2">
                  <ContactButtons phone={b.phone} testid={b.id} />
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button variant="outline" size="sm" className="text-secondary" onClick={() => openProfile(b.id)} data-testid={`view-profile-${b.id}`}><Eye className="h-4 w-4 mr-1.5" /> {t("admin.viewProfile")}</Button>
                    {b.blocked ? (
                      <Button variant="outline" size="sm" className="text-secondary" onClick={() => blockUser(b.id, false)} data-testid={`unblock-${b.id}`}><Power className="h-4 w-4 mr-1.5" />{t("admin.unblock")}</Button>
                    ) : (
                      <Button variant="outline" size="sm" className="text-destructive" onClick={() => blockUser(b.id, true)} data-testid={`block-${b.id}`}><Ban className="h-4 w-4 mr-1.5" />{t("admin.block")}</Button>
                    )}
                    <DeleteProfileButton userId={b.id} onDelete={deleteUser} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Offres (RFQ moderation) */}
        <TabsContent value="offers" className="mt-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-sm text-muted-foreground">{t("admin.offersManageHint")}</p>
            <Button className="bg-primary hover:bg-primary/90 rounded-full shrink-0" onClick={openCreateOffer} data-testid="admin-create-offer-btn">
              <Megaphone className="h-4 w-4 mr-1.5" /> {t("admin.offerCreate")}
            </Button>
            <Dialog open={offerDialogOpen} onOpenChange={(v) => { setOfferDialogOpen(v); if (!v) setEditId(null); }}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-offer-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-secondary">{editId ? t("admin.offerEditTitle") : t("admin.offerCreateTitle")}</DialogTitle>
                  <DialogDescription>{editId ? t("admin.offerEditDesc") : t("admin.offerCreateDesc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {!editId && (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label>{t("admin.offerBuyer")} <span className="text-muted-foreground font-normal">({t("admin.optional")})</span></Label>
                        <Select value={offerForm.buyer_id} onValueChange={(v) => setOfferForm((f) => ({ ...f, buyer_id: v }))}>
                          <SelectTrigger className="mt-1.5 h-11" data-testid="admin-offer-buyer"><SelectValue placeholder={t("admin.offerPickBuyer")} /></SelectTrigger>
                          <SelectContent>
                            {buyers.map((b) => (
                              <SelectItem key={b.id} value={b.id}>{b.name}{b.city ? ` — ${b.city}` : ""}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{t("admin.offerExternalBuyer")}</Label>
                        <Input value={offerForm.buyer_name} onChange={(e) => setOfferForm((f) => ({ ...f, buyer_name: e.target.value, buyer_id: e.target.value ? "" : f.buyer_id }))}
                          disabled={!!offerForm.buyer_id} placeholder={t("admin.offerExternalBuyerPh")} className="mt-1.5 h-11" data-testid="admin-offer-external-name" />
                      </div>
                    </div>
                  )}
                  {editId && offerForm.external_buyer && (
                    <div>
                      <Label>{t("admin.offerExternalBuyer")}</Label>
                      <Input value={offerForm.buyer_name} onChange={(e) => setOfferForm((f) => ({ ...f, buyer_name: e.target.value }))} placeholder={t("admin.offerExternalBuyerPh")} className="mt-1.5 h-11" data-testid="admin-offer-external-name" />
                    </div>
                  )}
                  {editId && !offerForm.external_buyer && (
                    <p className="text-sm text-muted-foreground" data-testid="admin-offer-buyer-readonly">{t("admin.offerBuyer")}: <span className="font-medium text-foreground">{offerForm.buyer_name}</span></p>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t("admin.offerProduct")}</Label>
                      <Select value={offerForm.category} onValueChange={(v) => setOfferForm((f) => ({ ...f, category: v }))}>
                        <SelectTrigger className="mt-1.5 h-11" data-testid="admin-offer-category"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{t(`categories.${c.slug}`)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t("admin.offerQuantity")}</Label>
                      <Input type="number" min={1} value={offerForm.quantity} onChange={(e) => setOfferForm((f) => ({ ...f, quantity: e.target.value }))} className="mt-1.5 h-11" data-testid="admin-offer-quantity" />
                    </div>
                  </div>
                  <div>
                    <Label>{t("admin.offerLocation")}</Label>
                    <div className="mt-1.5">
                      <CascadeSelect region={offerForm.region} city={offerForm.commune} neighborhood={offerForm.neighborhood}
                        onRegion={(v) => setOfferForm((f) => ({ ...f, region: v }))}
                        onCity={(v) => setOfferForm((f) => ({ ...f, commune: v }))}
                        onNeighborhood={(v) => setOfferForm((f) => ({ ...f, neighborhood: v }))}
                        testid="admin-offer-cascade" />
                    </div>
                  </div>
                  <div>
                    <Label>{t("admin.offerDescription")}</Label>
                    <Textarea value={offerForm.note} onChange={(e) => setOfferForm((f) => ({ ...f, note: e.target.value }))} rows={3} className="mt-1.5" placeholder={t("admin.offerDescriptionPh")} data-testid="admin-offer-note" />
                  </div>
                  {editId ? (
                    <div>
                      <Label>{t("admin.offerStatus")}</Label>
                      <Select value={offerForm.status} onValueChange={(v) => setOfferForm((f) => ({ ...f, status: v }))}>
                        <SelectTrigger className="mt-1.5 h-11" data-testid="admin-offer-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["pending", "published", "paused", "suspended"].map((s) => (
                            <SelectItem key={s} value={s}>{t(`offers.status${s.charAt(0).toUpperCase() + s.slice(1)}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2.5 cursor-pointer text-sm" data-testid="admin-offer-publish-toggle">
                      <input type="checkbox" checked={offerForm.publish} onChange={(e) => setOfferForm((f) => ({ ...f, publish: e.target.checked }))} className="h-4 w-4 accent-primary" />
                      <span className="text-foreground">{t("admin.offerPublishNow")}</span>
                    </label>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setOfferDialogOpen(false); setEditId(null); }}>{t("common.cancel")}</Button>
                  <Button onClick={submitOffer} disabled={offerBusy} className="bg-primary hover:bg-primary/90" data-testid="admin-offer-submit">
                    {offerBusy ? "..." : (editId ? t("admin.offerSaveEdit") : t("admin.offerCreate"))}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid gap-3">
            {offers.length === 0 ? <p className="text-muted-foreground text-sm py-10 text-center" data-testid="no-offers">{t("admin.noOffers")}</p> : offers.map((o) => (
              <div key={o.id} className="rounded-xl bg-card border border-border p-4" data-testid={`admin-offer-${o.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <img src={resolvePhoto(o.category_image)} alt={o.category_name} className="h-14 w-14 rounded-lg object-cover" />
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-secondary flex items-center gap-2 flex-wrap">
                        {o.category_name}
                        <Badge className={
                          o.status === "published" ? "bg-secondary/10 text-secondary border-0"
                          : o.status === "pending" ? "bg-amber-500/15 text-amber-700 border-0"
                          : o.status === "suspended" ? "bg-destructive/10 text-destructive border-0"
                          : "bg-muted text-muted-foreground border-0"
                        }>{t(`offers.status${o.status.charAt(0).toUpperCase() + o.status.slice(1)}`)}</Badge>
                        {o.created_by_admin && <Badge className="bg-primary/10 text-primary border-0" data-testid={`offer-admin-tag-${o.id}`}>{t("admin.offerByAdmin")}</Badge>}
                        {o.external_buyer && <Badge className="bg-amber-500/15 text-amber-700 border-0" data-testid={`offer-external-tag-${o.id}`}>{t("admin.offerExternalTag")}</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {t("admin.buyer")}: {o.buyer_name} · {o.quantity} · {[o.neighborhood, o.commune].filter(Boolean).join(", ")}{o.needed_by ? ` · ${formatDateFr(o.needed_by)}` : ""}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{o.responses_count} {t("offers.responsesLabel")}{o.note ? ` · “${o.note}”` : ""}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => openEditOffer(o)} data-testid={`offer-edit-${o.id}`}><Pencil className="h-4 w-4 mr-1.5" />{t("admin.edit")}</Button>
                    {(o.status === "pending" || o.status === "suspended" || o.status === "paused") && (
                      <Button size="sm" className="bg-secondary hover:bg-secondary/90" onClick={() => setOfferStatus(o.id, "published")} data-testid={`offer-publish-${o.id}`}><Check className="h-4 w-4 mr-1.5" />{t("admin.offerPublish")}</Button>
                    )}
                    {o.status === "published" && (
                      <>
                        <Button variant="outline" size="sm" className="text-destructive" onClick={() => setOfferStatus(o.id, "suspended")} data-testid={`offer-suspend-${o.id}`}><ShieldX className="h-4 w-4 mr-1.5" />{t("admin.offerSuspend")}</Button>
                        <Button variant="outline" size="sm" onClick={() => setOfferStatus(o.id, "paused")} data-testid={`offer-pause-${o.id}`}><Pause className="h-4 w-4 mr-1.5" />{t("admin.offerPause")}</Button>
                      </>
                    )}
                    <ConfirmDeleteButton
                      onConfirm={() => deleteOffer(o.id)}
                      testid={`offer-delete-${o.id}`}
                      label={t("admin.delete")}
                      title={t("admin.deleteOfferConfirmTitle")}
                      desc={t("admin.deleteOfferConfirmDesc")}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="orders" className="mt-6">
          <div className="grid gap-3">
            {orders.length === 0 ? <p className="text-muted-foreground text-sm py-10 text-center" data-testid="admin-orders-empty">{t("admin.noOrders")}</p> : orders.map((o) => {
              const needsAction = o.status === "pending_fee" || o.payment_status === "proof_submitted";
              return (
              <div key={o.id} className={`rounded-xl border p-4 ${needsAction ? "border-destructive/50 bg-destructive/5 ring-1 ring-destructive/20" : "bg-card border-border"}`} data-testid={`admin-order-${o.id}`}>
                <div className="flex items-center gap-4">
                  <img src={resolvePhoto(o.listing_image)} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-heading font-semibold text-secondary truncate">{o.listing_title}</p>
                      {needsAction && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-destructive text-white" data-testid={`order-todo-${o.id}`}>{t("admin.toProcess")}</span>}
                    </div>
                    <p className="text-sm text-muted-foreground">{o.buyer_name} → {o.seller_name} · {o.quantity}×</p>
                    {o.created_at && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1" data-testid={`order-created-${o.id}`}><Calendar className="h-3 w-3" /> {t("admin.createdAt")} : {formatDateTimeFr(o.created_at)}</p>}
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-bold text-primary">{formatPrice(o.grand_total ?? o.total_amount, cur)}</p>
                    <Badge className="bg-muted text-muted-foreground border-0" data-testid={`admin-order-status-${o.id}`}>{t(`order.statuses.${o.status}`)}</Badge>
                  </div>
                </div>
                <div className="mt-2 flex flex-col gap-1.5" data-testid={`order-contacts-${o.id}`}>
                  <ContactButtons phone={o.seller_phone} label={t("admin.seller")} testid={`os-${o.id}`} />
                  <ContactButtons phone={o.buyer_phone} label={t("admin.buyer")} testid={`ob-${o.id}`} />
                </div>
                {o.status === "pending_fee" && (
                  <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`fee-row-${o.id}`}>
                    <span className="text-sm text-muted-foreground">{t("admin.setFee")} :</span>
                    <input type="number" min={0} value={feeInputs[o.id] ?? ""} onChange={(e) => setFeeInputs((s) => ({ ...s, [o.id]: e.target.value }))} className="h-10 w-32 rounded-lg border border-border px-3 text-sm" placeholder="FCFA" data-testid={`fee-input-${o.id}`} />
                    <Button size="sm" className="bg-secondary hover:bg-secondary/90" onClick={() => saveFee(o.id)} data-testid={`fee-save-${o.id}`}><Truck className="h-4 w-4 mr-1.5" /> {t("admin.setFeeBtn")}</Button>
                  </div>
                )}
                {o.payment_status === "proof_submitted" && (
                  <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`validate-row-${o.id}`}>
                    {o.proof && <a href={resolvePhoto(o.proof)} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1.5" data-testid={`view-receipt-${o.id}`}><ImageIcon className="h-4 w-4" /> {t("admin.viewReceipt")}</a>}
                    <Button size="sm" className="bg-success hover:bg-success/90 text-white" onClick={() => validatePayment(o.id)} data-testid={`validate-payment-${o.id}`}><Wallet className="h-4 w-4 mr-1.5" /> {t("admin.validatePayment")}</Button>
                  </div>
                )}
              </div>
            );})}
          </div>
        </TabsContent>

        {/* Disputes */}
        <TabsContent value="disputes" className="mt-6">
          {/* Default admin setting */}
          <div className="rounded-xl bg-secondary/5 border border-secondary/20 p-4 mb-4" data-testid="dispute-settings-card">
            <p className="font-heading font-bold text-secondary flex items-center gap-2"><ShieldPlus className="h-4 w-4" /> {t("admin.disputeDefaultTitle")}</p>
            <p className="text-sm text-muted-foreground mt-0.5 mb-3">{t("admin.disputeDefaultHint")}</p>
            <div className="max-w-xs">
              <Select value={disputeSettings.default_admin_id || ""} onValueChange={setDefaultDisputeAdmin}>
                <SelectTrigger data-testid="dispute-default-admin-select"><SelectValue placeholder={t("admin.disputeDefaultPlaceholder")} /></SelectTrigger>
                <SelectContent>
                  {(disputeSettings.admins || []).map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`dispute-default-opt-${a.id}`}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-3">
            {disputes.length === 0 ? <p className="text-muted-foreground text-sm py-10 text-center" data-testid="no-disputes">{t("admin.noDisputes")}</p> : disputes.map((d) => (
              <div key={d.id} className={`rounded-xl border p-4 ${!d.status.startsWith("resolved") ? "border-destructive/50 bg-destructive/5 ring-1 ring-destructive/20" : "bg-card border-border"}`} data-testid={`admin-dispute-${d.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="font-heading font-semibold text-secondary flex items-center gap-2"><Gavel className="h-4 w-4" /> {d.listing_title}</p>
                  <Badge className={d.status === "escalated" ? "bg-destructive/10 text-destructive border-0" : d.status.startsWith("resolved") ? "bg-secondary/10 text-secondary border-0" : "bg-accent/15 text-accent-foreground border-0"}>{d.status}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{d.buyer_name} ↔ {d.seller_name}</p>
                <p className="text-sm text-foreground/70 mt-1">{d.reason}</p>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground" data-testid={`dispute-dates-${d.id}`}>
                  {d.created_at && <span>{t("dispute.openedAt")} : {formatDateTimeFr(d.created_at)}</span>}
                  {d.resolved_at && <span>{t("dispute.resolvedAt")} : {formatDateTimeFr(d.resolved_at)}</span>}
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-xs" data-testid={`dispute-assigned-${d.id}`}>
                  <ShieldCheck className="h-3.5 w-3.5 text-secondary" />
                  <span className="text-muted-foreground">{t("admin.disputeAssigned")} :</span>
                  <span className="font-semibold text-secondary">{d.assigned_admin_name || t("admin.disputeUnassigned")}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  <Button size="sm" variant="outline" className="text-secondary" onClick={() => { setThreadDispute(d); setDmsg(""); }} data-testid={`open-dispute-convo-${d.id}`}>
                    <MessagesSquare className="h-4 w-4 mr-1.5" /> {t("admin.disputeOpenConvo")}
                  </Button>
                  <div className="w-52">
                    <Select value={d.assigned_admin_id || ""} onValueChange={(v) => transferDispute(d.id, v)}>
                      <SelectTrigger className="h-9" data-testid={`dispute-transfer-select-${d.id}`}><SelectValue placeholder={t("admin.disputeTransferPlaceholder")} /></SelectTrigger>
                      <SelectContent>
                        {(disputeSettings.admins || []).map((a) => (
                          <SelectItem key={a.id} value={a.id} data-testid={`dispute-transfer-opt-${d.id}-${a.id}`}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {!d.status.startsWith("resolved") && (
                    <>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => resolveDispute(d.id, "refund")} data-testid={`resolve-refund-${d.id}`}>{t("admin.resolveRefund")}</Button>
                      <Button size="sm" className="bg-secondary hover:bg-secondary/90" onClick={() => resolveDispute(d.id, "release")} data-testid={`resolve-release-${d.id}`}>{t("admin.resolveRelease")}</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Dispute conversation dialog */}
          <Dialog open={!!threadDispute} onOpenChange={(v) => { if (!v) setThreadDispute(null); }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dispute-convo-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-secondary flex items-center gap-2"><Gavel className="h-5 w-5" /> {threadDispute?.listing_title}</DialogTitle>
                <DialogDescription>{threadDispute?.buyer_name} ↔ {threadDispute?.seller_name}</DialogDescription>
              </DialogHeader>
              {threadDispute && (
                <>
                  <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="dispute-convo-thread">
                    {(threadDispute.messages || []).map((m, i) => (
                      <div key={i} className={`text-sm rounded-lg px-3 py-2 ${m.sender_role === "system" ? "bg-muted text-muted-foreground italic" : m.sender_role === "admin" ? "bg-secondary text-secondary-foreground" : m.sender_role === "buyer" ? "bg-primary/10" : "bg-card border border-border"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] uppercase font-semibold opacity-70">{m.sender_role}</span>
                          {m.at && <span className="text-[10px] opacity-60">{formatDateTimeFr(m.at)}</span>}
                        </div>
                        {m.body && <p>{m.body}</p>}
                        {m.images?.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-2">
                            {m.images.map((img, j) => (
                              <a key={j} href={resolvePhoto(img)} target="_blank" rel="noreferrer" className="block h-20 w-20 rounded-lg overflow-hidden border border-border">
                                <img src={resolvePhoto(img)} alt="" className="h-full w-full object-cover pointer-events-none" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {!threadDispute.status.startsWith("resolved") && (
                    <div className="mt-3 flex gap-2">
                      <Input value={dmsg} onChange={(e) => setDmsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendDisputeMsg(); }} placeholder={t("admin.disputeMsgPlaceholder")} data-testid="dispute-convo-input" />
                      <Button onClick={sendDisputeMsg} disabled={dmsgBusy} className="bg-primary hover:bg-primary/90" data-testid="dispute-convo-send">{t("admin.disputeSend")}</Button>
                    </div>
                  )}
                </>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="mt-6">
          <div className="grid gap-3">
            {history.length === 0 ? <p className="text-muted-foreground text-sm py-10 text-center" data-testid="no-history">{t("admin.noHistory")}</p> : history.map((o) => (
              <div key={o.id} className="rounded-xl bg-card border border-border p-4 flex items-center gap-4" data-testid={`admin-history-${o.id}`}>
                <img src={resolvePhoto(o.listing_image)} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-secondary truncate">{o.listing_title}</p>
                  <p className="text-sm text-muted-foreground">{o.buyer_name} → {o.seller_name} · {new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
                <div className="text-right">
                  <p className="font-heading font-bold text-primary">{formatPrice(o.grand_total ?? o.total_amount, cur)}</p>
                  <Badge className="bg-muted text-muted-foreground border-0">{t(`order.statuses.${o.status}`)}</Badge>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Finance */}
        <TabsContent value="finance" className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard Icon={Wallet} label={t("admin.totalReceived")} value={formatPrice(finance.totals.total_received || 0, cur)} />
            <StatCard Icon={Gem} label={t("admin.commission")} value={formatPrice(finance.totals.commission || 0, cur)} />
            <StatCard Icon={Truck} label={t("admin.toPayout")} value={formatPrice(finance.totals.net_to_seller || 0, cur)} />
          </div>
          <h3 className="font-heading font-bold text-lg text-secondary mb-3">{t("admin.payoutsTitle")}</h3>
          <div className="grid gap-2 mb-8">
            {finance.payouts.length === 0 ? <p className="text-muted-foreground text-sm">{t("admin.noFinance")}</p> : finance.payouts.map((p) => (
              <div key={p.seller_id} className="rounded-xl bg-success/5 border border-success/30 p-4 flex items-center justify-between" data-testid={`payout-${p.seller_id}`}>
                <span className="font-semibold text-secondary">{p.seller_name} <span className="text-xs text-muted-foreground">({p.orders})</span></span>
                <span className="font-heading font-bold text-success">{formatPrice(p.amount, cur)} {t("admin.toPay")}</span>
              </div>
            ))}
          </div>
          <div className="grid gap-2">
            {finance.orders.map((o) => (
              <div key={o.id} className="rounded-xl bg-card border border-border p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`finance-order-${o.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-secondary truncate">{o.listing_title}</p>
                  <p className="text-xs text-muted-foreground">{o.seller_name} · {t("admin.totalReceived")}: {formatPrice(o.total_received, cur)} · {t("admin.commission")}: {formatPrice(o.commission, cur)} · {t("admin.toPayout")}: {formatPrice(o.net_to_seller, cur)}</p>
                </div>
                {o.status === "delivered"
                  ? <Button size="sm" className="bg-secondary hover:bg-secondary/90" onClick={() => closeOrder(o.id)} data-testid={`close-order-${o.id}`}>{t("admin.closeOrderBtn")}</Button>
                  : <Badge className="bg-success/15 text-success border-0">{t("admin.closed")}</Badge>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="rst" className="mt-6"><RstAdminPanel /></TabsContent>

        {/* Anti-contact violations */}
        <TabsContent value="violations" className="mt-6">
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-4 flex items-start gap-2.5" data-testid="violations-intro">
            <ShieldX className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-heading font-semibold text-secondary">{t("admin.violationsTitle")}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{t("admin.violationsHint", { n: violations.total_attempts })}</p>
            </div>
          </div>
          {violations.offenders.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="no-violations">{t("admin.noViolations")}</p>
          ) : (
            <div className="grid gap-3">
              {violations.offenders.map((v) => (
                <div key={v.user_id} className="rounded-2xl bg-card border border-border p-4" data-testid={`violation-${v.user_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-secondary flex items-center gap-2">
                        {v.user_name}
                        <Badge className={v.user_role === "seller" ? "bg-secondary/10 text-secondary border-0" : "bg-primary/10 text-primary border-0"}>{t(v.user_role === "seller" ? "admin.roleSeller" : "admin.roleBuyer")}</Badge>
                        {v.blocked && <Badge className="bg-destructive/10 text-destructive border-0">{t("admin.blockedLabel")}</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        <span className="font-bold text-destructive" data-testid={`violation-count-${v.user_id}`}>{t("admin.violationsAttempts", { n: v.count })}</span>
                        {v.city ? ` · ${v.city}` : ""}{v.last_at ? ` · ${t("admin.violationsLast")}: ${formatDateFr(v.last_at.slice(0, 10))}` : ""}
                      </p>
                    </div>
                    <div className="shrink-0">
                      {v.blocked ? (
                        <Button variant="outline" size="sm" className="text-secondary" onClick={() => blockUser(v.user_id, false)} data-testid={`violation-unblock-${v.user_id}`}><Power className="h-4 w-4 mr-1.5" />{t("admin.unblock")}</Button>
                      ) : (
                        <Button variant="outline" size="sm" className="text-destructive" onClick={() => blockUser(v.user_id, true)} data-testid={`violation-block-${v.user_id}`}><Ban className="h-4 w-4 mr-1.5" />{t("admin.block")}</Button>
                      )}
                    </div>
                  </div>
                  {v.snippets?.length > 0 && (
                    <div className="mt-2.5 space-y-1">
                      {v.snippets.map((s, i) => (
                        <p key={i} className="text-xs text-foreground/70 bg-muted rounded-lg px-2.5 py-1.5 italic line-clamp-1">“{s}”</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} data={profileData} loading={profileLoading} cur={cur} />
    </div>
  );
}
